import { useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Loader2, X, CreditCard, Infinity as InfinityIcon, Sparkles, Wand2, Lightbulb, AtSign } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { cn } from '../lib/cn';
import { PLATFORMS, PLATFORM_META, type Platform } from '../lib/platforms';

/* ── Types ──────────────────────────────────────────────── */
type PlanNetwork = { platform: string; maxAccounts: number };
type Plan = {
  id: string;
  name: string;
  description: string | null;
  priceMonthly: number; // cents
  priceYearly: number;  // cents
  currency: string;
  unlimitedAccounts: boolean;
  aiPredictionsMonthly: number;
  aiCaptionsMonthly: number;
  aiIdeasMonthly: number;
  bioPagesLimit: number | null; // null = unlimited
  teamMembersLimit: number | null; // 0 = no teams, null = unlimited
  active: boolean;
  sortOrder: number;
  createdAt: string;
  networks: PlanNetwork[];
};

/* ── Money helpers ──────────────────────────────────────── */
const centsToInput = (c: number) => (c / 100).toFixed(2);
const inputToCents = (s: string) => {
  const n = parseFloat(s.replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : 0;
};
const fmtMoney = (cents: number, currency: string) =>
  (cents / 100).toLocaleString('es-ES', { style: 'currency', currency });

/* ── Main page ──────────────────────────────────────────── */
export function AdminPlans() {
  const [editing, setEditing] = useState<Plan | null | 'new'>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'plans'],
    queryFn: () => api<{ plans: Plan[] }>('/api/admin/plans'),
  });
  const plans = data?.plans ?? [];

  return (
    <div className="space-y-6">
      {/* header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Planes</h1>
          <p className="mt-1 text-sm text-slate-500">
            Define los planes de suscripción, sus redes y límites de cuentas.
          </p>
        </div>
        <button onClick={() => setEditing('new')} className="btn-primary gap-2">
          <Plus size={16} /> Crear plan
        </button>
      </div>

      {/* list */}
      {isLoading ? (
        <div className="flex items-center justify-center gap-2 text-slate-400 py-16">
          <Loader2 size={16} className="animate-spin" /> Cargando planes…
        </div>
      ) : plans.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm py-16 flex flex-col items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center">
            <CreditCard size={22} className="text-slate-400" strokeWidth={1.5} />
          </div>
          <p className="text-sm text-slate-500">Todavía no has creado ningún plan.</p>
          <button onClick={() => setEditing('new')} className="btn-primary gap-2 mt-1">
            <Plus size={16} /> Crear plan
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {plans.map((p) => (
            <PlanCard key={p.id} plan={p} onEdit={() => setEditing(p)} />
          ))}
        </div>
      )}

      {editing && (
        <PlanModal
          plan={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/* ── Plan card ──────────────────────────────────────────── */
function PlanCard({ plan, onEdit }: { plan: Plan; onEdit: () => void }) {
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: () => api(`/api/admin/plans/${plan.id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'plans'] }),
    onError: (err) => {
      window.alert(
        err instanceof ApiError && err.code === 'PLAN_HAS_SUBSCRIPTIONS'
          ? 'No se puede eliminar: el plan tiene suscripciones asociadas. Desactívalo en su lugar.'
          : 'No se pudo eliminar el plan.',
      );
    },
  });

  function onDelete() {
    if (window.confirm(`¿Eliminar el plan «${plan.name}»? Esta acción no se puede deshacer.`)) {
      del.mutate();
    }
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="font-bold text-slate-900 truncate">{plan.name}</h2>
            <span className={cn(
              'text-[11px] font-medium rounded-full px-2 py-0.5 border',
              plan.active
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-slate-100 text-slate-500 border-slate-200',
            )}>
              {plan.active ? 'Activo' : 'Inactivo'}
            </span>
          </div>
          {plan.description && (
            <p className="mt-1 text-sm text-slate-500 line-clamp-2">{plan.description}</p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={onEdit} title="Editar"
            className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 transition">
            <Pencil size={14} />
          </button>
          <button onClick={onDelete} title="Eliminar" disabled={del.isPending}
            className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-slate-200 text-red-500 hover:bg-red-50 transition disabled:opacity-50">
            {del.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
          </button>
        </div>
      </div>

      {/* prices */}
      <div className="flex items-baseline gap-3">
        <span className="text-2xl font-extrabold text-slate-900">
          {fmtMoney(plan.priceMonthly, plan.currency)}
        </span>
        <span className="text-sm text-slate-400">/mes</span>
        <span className="text-sm text-slate-500">· {fmtMoney(plan.priceYearly, plan.currency)}/año</span>
      </div>

      {/* networks */}
      <div className="border-t border-slate-100 pt-3">
        {plan.networks.length === 0 ? (
          <p className="text-xs text-slate-400">Sin redes sociales asignadas.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {plan.networks.map((n) => {
              const meta = PLATFORM_META[n.platform as Platform];
              if (!meta) return null;
              const Icon = meta.icon;
              return (
                <span key={n.platform}
                  className="inline-flex items-center gap-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-slate-600">
                  <Icon size={13} color={meta.color} />
                  {plan.unlimitedAccounts
                    ? <InfinityIcon size={12} className="text-slate-400" />
                    : <span className="font-semibold tabular-nums">{n.maxAccounts}</span>}
                </span>
              );
            })}
          </div>
        )}
        {plan.unlimitedAccounts && (
          <p className="mt-2 text-xs text-brand-600 font-medium flex items-center gap-1">
            <InfinityIcon size={12} /> Cuentas ilimitadas
          </p>
        )}
      </div>

      {/* Link in bio quota */}
      {(plan.bioPagesLimit === null || plan.bioPagesLimit > 0) && (
        <div className="border-t border-slate-100 pt-3 flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs bg-violet-50 border border-violet-100 text-violet-800 rounded-lg px-2 py-1">
            <AtSign size={12} className="text-violet-600" />
            {plan.bioPagesLimit === null ? (
              <>
                <InfinityIcon size={12} />
                <span className="text-violet-700/80">link in bio</span>
              </>
            ) : (
              <>
                <span className="font-semibold tabular-nums">{plan.bioPagesLimit}</span>
                <span className="text-violet-700/80">
                  link in bio{plan.bioPagesLimit === 1 ? '' : 's'}
                </span>
              </>
            )}
          </span>
        </div>
      )}

      {/* AI quotas */}
      {(plan.aiIdeasMonthly > 0 || plan.aiCaptionsMonthly > 0 || plan.aiPredictionsMonthly > 0) && (
        <div className="border-t border-slate-100 pt-3 flex flex-wrap gap-2">
          {plan.aiIdeasMonthly > 0 && (
            <span className="inline-flex items-center gap-1.5 text-xs bg-amber-50 border border-amber-100 text-amber-800 rounded-lg px-2 py-1">
              <Lightbulb size={12} className="text-amber-600" />
              <span className="font-semibold tabular-nums">{plan.aiIdeasMonthly}</span>
              <span className="text-amber-700/80">ideas/mes</span>
            </span>
          )}
          {plan.aiCaptionsMonthly > 0 && (
            <span className="inline-flex items-center gap-1.5 text-xs bg-orange-50 border border-orange-100 text-orange-800 rounded-lg px-2 py-1">
              <Wand2 size={12} className="text-orange-600" />
              <span className="font-semibold tabular-nums">{plan.aiCaptionsMonthly}</span>
              <span className="text-orange-700/80">captions/mes</span>
            </span>
          )}
          {plan.aiPredictionsMonthly > 0 && (
            <span className="inline-flex items-center gap-1.5 text-xs bg-brand-50 border border-brand-100 text-brand-800 rounded-lg px-2 py-1">
              <Sparkles size={12} className="text-brand-600" />
              <span className="font-semibold tabular-nums">{plan.aiPredictionsMonthly}</span>
              <span className="text-brand-700/80">predicciones/mes</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Create / edit modal ────────────────────────────────── */
type NetState = Record<Platform, { enabled: boolean; max: string }>;

function emptyNetworks(): NetState {
  return Object.fromEntries(
    PLATFORMS.map((p) => [p, { enabled: false, max: '1' }]),
  ) as NetState;
}

function networksFromPlan(plan: Plan): NetState {
  const base = emptyNetworks();
  for (const n of plan.networks) {
    if (PLATFORMS.includes(n.platform as Platform)) {
      base[n.platform as Platform] = { enabled: true, max: String(n.maxAccounts) };
    }
  }
  return base;
}

function PlanModal({ plan, onClose }: { plan: Plan | null; onClose: () => void }) {
  const qc = useQueryClient();
  const isEdit = plan !== null;

  const [name, setName]               = useState(plan?.name ?? '');
  const [description, setDescription] = useState(plan?.description ?? '');
  const [monthly, setMonthly]         = useState(plan ? centsToInput(plan.priceMonthly) : '0.00');
  const [yearly, setYearly]           = useState(plan ? centsToInput(plan.priceYearly) : '0.00');
  const [unlimited, setUnlimited]     = useState(plan?.unlimitedAccounts ?? false);
  const [aiIdeas, setAiIdeas]         = useState(String(plan?.aiIdeasMonthly ?? 0));
  const [aiCaptions, setAiCaptions]   = useState(String(plan?.aiCaptionsMonthly ?? 0));
  const [aiPreds, setAiPreds]         = useState(String(plan?.aiPredictionsMonthly ?? 0));
  // Link-in-bio quota. "" / null means unlimited; an integer means a hard cap.
  const [bioUnlimited, setBioUnlimited] = useState(plan ? plan.bioPagesLimit === null : false);
  const [bioLimit, setBioLimit]         = useState(String(plan?.bioPagesLimit ?? 1));
  // Team seats. null = unlimited; 0 = teams disabled; N = N extra members.
  const [teamUnlimited, setTeamUnlimited] = useState(plan ? plan.teamMembersLimit === null : false);
  const [teamLimit, setTeamLimit]         = useState(String(plan?.teamMembersLimit ?? 0));
  const [active, setActive]           = useState(plan?.active ?? false);
  const [nets, setNets]               = useState<NetState>(plan ? networksFromPlan(plan) : emptyNetworks());
  const [error, setError]             = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (body: unknown) =>
      api(isEdit ? `/api/admin/plans/${plan!.id}` : '/api/admin/plans', {
        method: isEdit ? 'PATCH' : 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'plans'] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? 'Revisa los datos del plan.' : 'No se pudo guardar el plan.');
    },
  });

  function toggleNet(p: Platform) {
    setNets((prev) => ({ ...prev, [p]: { ...prev[p], enabled: !prev[p].enabled } }));
  }
  function setNetMax(p: Platform, max: string) {
    setNets((prev) => ({ ...prev, [p]: { ...prev[p], max } }));
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (name.trim().length === 0) { setError('El plan necesita un nombre.'); return; }

    const networks = PLATFORMS
      .filter((p) => nets[p].enabled)
      .map((p) => ({
        platform: p,
        maxAccounts: Math.max(0, parseInt(nets[p].max, 10) || 0),
      }));

    save.mutate({
      name: name.trim(),
      description: description.trim() || null,
      priceMonthly: inputToCents(monthly),
      priceYearly: inputToCents(yearly),
      currency: 'EUR',
      unlimitedAccounts: unlimited,
      aiIdeasMonthly: Math.max(0, parseInt(aiIdeas, 10) || 0),
      aiCaptionsMonthly: Math.max(0, parseInt(aiCaptions, 10) || 0),
      aiPredictionsMonthly: Math.max(0, parseInt(aiPreds, 10) || 0),
      bioPagesLimit: bioUnlimited ? null : Math.max(0, parseInt(bioLimit, 10) || 0),
      teamMembersLimit: teamUnlimited ? null : Math.max(0, parseInt(teamLimit, 10) || 0),
      active,
      networks,
    });
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white">
          <h2 className="font-bold text-slate-900">{isEdit ? 'Editar plan' : 'Crear plan'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="px-6 py-5 space-y-5">
          {/* name */}
          <div>
            <label className="label" htmlFor="plan-name">Nombre</label>
            <input id="plan-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Ej. Starter, Pro…" className="input" />
          </div>

          {/* description */}
          <div>
            <label className="label" htmlFor="plan-desc">Descripción <span className="text-slate-400 font-normal">(opcional)</span></label>
            <textarea id="plan-desc" value={description} onChange={(e) => setDescription(e.target.value)}
              rows={2} className="input resize-none" />
          </div>

          {/* prices */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label" htmlFor="plan-monthly">Precio mensual (€)</label>
              <input id="plan-monthly" type="number" min="0" step="0.01"
                value={monthly} onChange={(e) => setMonthly(e.target.value)} className="input" />
            </div>
            <div>
              <label className="label" htmlFor="plan-yearly">Precio anual (€)</label>
              <input id="plan-yearly" type="number" min="0" step="0.01"
                value={yearly} onChange={(e) => setYearly(e.target.value)} className="input" />
            </div>
          </div>

          {/* unlimited */}
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input type="checkbox" checked={unlimited} onChange={(e) => setUnlimited(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
            <span className="text-sm font-medium text-slate-700">Cuentas ilimitadas</span>
            <span className="text-xs text-slate-400">— ignora los límites por red</span>
          </label>

          {/* AI quotas */}
          <div>
            <p className="label">Cuotas IA mensuales</p>
            <p className="text-xs text-slate-400 -mt-1 mb-2">Pon 0 para ocultar la función a usuarios de este plan.</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="flex items-center gap-1.5 text-xs font-medium text-slate-700 mb-1" htmlFor="plan-ai-ideas">
                  <Lightbulb size={12} className="text-amber-500" /> Ideas
                </label>
                <input id="plan-ai-ideas" type="number" min="0" step="1"
                  value={aiIdeas} onChange={(e) => setAiIdeas(e.target.value)} className="input" />
              </div>
              <div>
                <label className="flex items-center gap-1.5 text-xs font-medium text-slate-700 mb-1" htmlFor="plan-ai-captions">
                  <Wand2 size={12} className="text-accent-500" /> Captions
                </label>
                <input id="plan-ai-captions" type="number" min="0" step="1"
                  value={aiCaptions} onChange={(e) => setAiCaptions(e.target.value)} className="input" />
              </div>
              <div>
                <label className="flex items-center gap-1.5 text-xs font-medium text-slate-700 mb-1" htmlFor="plan-ai-preds">
                  <Sparkles size={12} className="text-brand-500" /> Predicciones
                </label>
                <input id="plan-ai-preds" type="number" min="0" step="1"
                  value={aiPreds} onChange={(e) => setAiPreds(e.target.value)} className="input" />
              </div>
            </div>
          </div>

          {/* Link in bio quota */}
          <div>
            <p className="label">Link in bio</p>
            <p className="text-xs text-slate-400 -mt-1 mb-2">
              Número de páginas zlink.club que este plan puede crear.
            </p>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={bioUnlimited}
                  onChange={(e) => setBioUnlimited(e.target.checked)}
                />
                Ilimitadas
              </label>
              {!bioUnlimited && (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={bioLimit}
                    onChange={(e) => setBioLimit(e.target.value)}
                    className="input w-24"
                    aria-label="Límite de páginas"
                  />
                  <span className="text-xs text-slate-500">páginas</span>
                </div>
              )}
            </div>
          </div>

          {/* Team members */}
          <div>
            <p className="label">Equipos</p>
            <p className="text-xs text-slate-400 -mt-1 mb-2">
              Miembros EXTRA que este plan permite añadir al equipo (0 = sin equipos).
            </p>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={teamUnlimited}
                  onChange={(e) => setTeamUnlimited(e.target.checked)}
                />
                Ilimitados
              </label>
              {!teamUnlimited && (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={teamLimit}
                    onChange={(e) => setTeamLimit(e.target.value)}
                    className="input w-24"
                    aria-label="Límite de miembros"
                  />
                  <span className="text-xs text-slate-500">miembros</span>
                </div>
              )}
            </div>
          </div>

          {/* networks */}
          <div>
            <p className="label">Redes sociales y nº de cuentas</p>
            <div className="border border-slate-200 rounded-xl divide-y divide-slate-100">
              {PLATFORMS.map((p) => {
                const meta = PLATFORM_META[p];
                const Icon = meta.icon;
                const net = nets[p];
                return (
                  <div key={p} className="flex items-center gap-3 px-3 py-2">
                    <label className="flex items-center gap-2.5 flex-1 cursor-pointer min-w-0">
                      <input type="checkbox" checked={net.enabled} onChange={() => toggleNet(p)}
                        className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
                      <Icon size={16} color={meta.color} className="shrink-0" />
                      <span className={cn('text-sm truncate', net.enabled ? 'text-slate-700' : 'text-slate-400')}>
                        {meta.label}
                      </span>
                    </label>
                    {net.enabled && (
                      unlimited ? (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                          <InfinityIcon size={13} /> ilimitadas
                        </span>
                      ) : (
                        <input
                          type="number" min="0" step="1"
                          value={net.max}
                          onChange={(e) => setNetMax(p, e.target.value)}
                          className="input !w-20 !py-1 text-sm"
                          aria-label={`Cuentas de ${meta.label}`}
                        />
                      )
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* active */}
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
            <span className="text-sm font-medium text-slate-700">Plan activo</span>
            <span className="text-xs text-slate-400">— visible y contratable</span>
          </label>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">{error}</p>
          )}

          {/* actions */}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
            <button type="submit" disabled={save.isPending} className="btn-primary gap-2">
              {save.isPending && <Loader2 size={14} className="animate-spin" />}
              {save.isPending ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear plan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
